import Api from '../api';
import {
  GET_ALL_SMS_HISTORY,
  GET_ALL_SMS_HISTORY_URL,
  GET_SMS_HISTORY, GET_SMS_HISTORY_URL,
  GET_TRACK_CAMPAIGN, GET_TRACK_CAMPAIGN_DELIVERY_URL,
  GET_MESSAGE_TEXT_URL,
  SMS_HISTORY_DWNL_FILTER_REPORT_JOB_ID,
  SMS_HISTORY_DWNL_FULL_REPORT_URL,
  SMS_HISTORY_DWNL_MESSAGES_DETAILED_REPORT_URL,
  SMS_HISTORY_DWNL_REPORT_JOB_STATUS_URL,
  SMS_HISTORY_DWNL_REPORT_URL,
  SMS_HISTORY_SMART_STATUS,
  SMS_HISTORY_SMART_STATUS_URL,
  SMS_HISTORY_STATUS,
  SMS_HISTORY_STATUS_URL
} from '../lib/constant';
import {Polling} from '../lib/poll';
import {getAuthToken, handleAPIDispatch, isBadReq, routeToLogin} from '../lib/utils';

// SMS Type (UI label) → report API message_type_filter value. The report
// backend expects TRANSACTIONAL / PROMOTIONAL / OTP (control_plane
// docs/SMS_REPORTS_ME_API.md). The SMS-Type dropdown carries wallet-derived
// labels (Transactional/Promotional/OTP) and may also surface TRANS/PROMO —
// note this differs from sms.js' smsTypeToApiFormat which yields TRANS/PROMO.
const smsTypeToMessageTypeFilter = {
  Transactional: 'TRANSACTIONAL', transactional: 'TRANSACTIONAL', TRANS: 'TRANSACTIONAL',
  Promotional: 'PROMOTIONAL', promotional: 'PROMOTIONAL', PROMO: 'PROMOTIONAL',
  OTP: 'OTP', otp: 'OTP',
};

export function getSMSHistory(params){
  // Transform request params to new API format
  const apiParams = {
    date_range: {
      start_date: params.startDate,
      end_date: params.endDate
    },
    sender_id_filter: params.senderId || []
  };
  if (params.mobileNo) apiParams.phone_number_search = params.mobileNo;
  // Server-side SMS Type filter (ListMyCampaigns.message_type_filter). 'ALL' =
  // no filter. Map the UI label to the backend TRANSACTIONAL/PROMOTIONAL/OTP.
  if (params.smsType && params.smsType !== 'ALL') {
    apiParams.message_type_filter = [
      smsTypeToMessageTypeFilter[params.smsType] || String(params.smsType).toUpperCase(),
    ];
  }
  // ListMyCampaigns.page / page_size (sms_reports_me.proto). Omitted until
  // 2026-09-09, so the server always answered page 1 of 50 and the reports
  // table could only ever show the first 50 of (e.g.) 66,750 campaigns.
  if (params.page) apiParams.page = params.page;
  if (params.page_size) apiParams.page_size = params.page_size;
  const request = Api.post(GET_SMS_HISTORY_URL, apiParams);
  return handleAPIDispatch(request, GET_SMS_HISTORY)
}

export function getAllSMSHistory(params){
  // Build API params with required date_range
  const apiParams = {
    date_range: params.date_range,
    page: params.page,
    page_size: params.page_size
  };
  // Add optional filters if provided
  if (params.sender_id_filter?.length) apiParams.sender_id_filter = params.sender_id_filter;
  if (params.message_type_filter?.length) apiParams.message_type_filter = params.message_type_filter;
  if (params.status_filter?.length) apiParams.status_filter = params.status_filter;
  if (params.phone_number_search) apiParams.phone_number_search = params.phone_number_search;
  if (params.cursor) apiParams.cursor = params.cursor;

  const request = Api.post(GET_ALL_SMS_HISTORY_URL, apiParams);
  return handleAPIDispatch(request, GET_ALL_SMS_HISTORY)
}

export function getStatusSMSHistory(id){
  let url = SMS_HISTORY_STATUS_URL.replace('{campaign_id}',id)
  const request = Api.get(url);
  return handleAPIDispatch(request, SMS_HISTORY_STATUS)
}

export function getSmartStatusSMSHistory(id){
  const request = Api.get(SMS_HISTORY_SMART_STATUS_URL.replace('tId',id));
  return handleAPIDispatch(request, SMS_HISTORY_SMART_STATUS)
}

export function getMessageText(campaignId, messageId){
  let url = GET_MESSAGE_TEXT_URL
    .replace("{campaign_id}", campaignId)
    .replace("{message_id}", messageId);
  const request = Api.get(url);
  return handleAPIDispatch(request, GET_TRACK_CAMPAIGN)
}

export function getTrackCampaignDelivery(id, params){
  let url = GET_TRACK_CAMPAIGN_DELIVERY_URL.replace("{campaign_id}",id);
  const request = Api.get(url, true,  params);
  return handleAPIDispatch(request, GET_TRACK_CAMPAIGN)
}

const responseHandler = (resp) => resp?.status === "COMPLETED" || resp?.status === "FAILED"

export async function getSMSReportSync(id){
  const url = SMS_HISTORY_DWNL_REPORT_URL.replace('{campaign_id}', id);
  try {
    // Submit the report generation job
    const {data} = await Api.post(url, {format: "EXCEL"});
    if (!data?.success || !data?.jobId) {
      throw {msg: "Failed to submit report job"};
    }
    // Poll for completion
    const jobStatusUrl = SMS_HISTORY_DWNL_REPORT_JOB_STATUS_URL.replace('{job_id}', data.jobId);
    const fetchParams = {
      method: 'GET',
      headers: { authorization: getAuthToken() }
    };
    const poll = new Polling(jobStatusUrl, fetchParams, responseHandler);
    const result = await poll.poll();
    if (result?.status === "COMPLETED" && result?.downloadUrl) {
      return result.downloadUrl;
    }
    throw {msg: "Report generation failed"};
  } catch (err) {
    if (isBadReq(err)) {
      routeToLogin();
    } else {
      throw err;
    }
  }
}

export function getSMSReportExportJobID(params){
  const apiParams = {
    dateRange: {
      startDate: params.startDate,
      endDate: params.endDate
    },
    senderIdFilter: params.senderIds && params.senderIds.length > 0 ? params.senderIds : [],
    format: "EXCEL"
  };
  if (params.mobileNo && params.mobileNo !== 'NA') {
    apiParams.phoneNumberSearch = params.mobileNo;
  }
  // Respect the SMS Type filter so the exported report matches the on-screen
  // list (parity with the campaign/report filter in the legacy flow). 'ALL' =
  // no filter. Map the UI label to the backend's TRANSACTIONAL/PROMOTIONAL/OTP.
  if (params.smsType && params.smsType !== 'ALL') {
    apiParams.messageTypeFilter = [
      smsTypeToMessageTypeFilter[params.smsType] || String(params.smsType).toUpperCase(),
    ];
  }
  // Dashboard download = per-message DETAILED report (one row per individual
  // SMS, legacy production-report shape). The campaign-summary endpoint at
  // SMS_HISTORY_DWNL_FULL_REPORT_URL is kept as a reference but not used
  // here — the surface users compare against in spreadsheets has always been
  // the per-message detail report.
  const request = Api.post(SMS_HISTORY_DWNL_MESSAGES_DETAILED_REPORT_URL, apiParams);
  return handleAPIDispatch(request, SMS_HISTORY_DWNL_FILTER_REPORT_JOB_ID)
}

export function getSMSReportPolling(jobId){
  let jobStatusUrl = SMS_HISTORY_DWNL_REPORT_JOB_STATUS_URL.replace('{job_id}',jobId)

  const fetchParams = {
    "method": 'GET',
    "headers": {
      "authorization": getAuthToken()
    }
  }
  const poll = new Polling(jobStatusUrl, fetchParams, responseHandler)
  return poll
}
